/**
 * DSD 편집기 문서 영역의 표 셀 드래그 선택/복사 훅.
 *
 * ixd-dsd 대사의 DSD 프리뷰와 동일한 공통 훅(useTableCellSelection)을 컨테이너 위임
 * 방식으로 사용한다. PlainDsdTableView 셀이 이미 selectableCellAttrs 계약
 * (id·data-cell-id·data-display-value)을 갖고 있어, 셀 쪽 변경 없이 이 훅을 문서
 * 스크롤 컨테이너에 달기만 하면 드래그 선택 + Ctrl+C 복사가 동작한다.
 *
 * 기존 클릭 액션(주석/교정/합계 셀 선택, 더블클릭 편집)과의 공존:
 * - 공통 훅은 mousedown/mouseover 로만 동작하고 click 은 삼키지 않는다 → 기존 액션 유지.
 * - 단일 클릭(선택 1개)에는 시각 강조를 칠하지 않는다(minSelectionToStyle=2) —
 *   기존 선택 ring 과 겹치지 않고, 드래그 2셀부터 복사용 다중 선택으로 취급한다.
 * - 편집기엔 자체 방향키 UX 가 없지만, 클릭 액션 표 프리셋(DSD 프리뷰)과 동작을
 *   맞추기 위해 방향키 이동은 끈다.
 */

import type { RefObject } from "react";
import {
  MULTI_SELECT_CELL_BORDER_CSS,
  useTableCellSelection,
} from "@/hooks/use-table-cell-selection";

export const DSD_EDITOR_SELECTION_STYLE_ID = "dsd-editor-cell-selection-style";

interface UseDsdEditorCellSelectionOptions {
  /**
   * 다중 선택(셀 2개 이상) 여부 변경 콜백. 드래그 종료 click 은 mousedown/mouseup
   * 타깃이 달라 공통 조상에서 발화하므로, 컨테이너의 "빈 영역 클릭 → 선택 해제"
   * onClick 이 이를 오인하지 않도록 호출부가 이 상태로 가드한다.
   */
  onMultiSelectChange?: (isMulti: boolean) => void;
}

export function useDsdEditorCellSelection(
  containerRef: RefObject<HTMLDivElement | null>,
  options?: UseDsdEditorCellSelectionOptions,
) {
  return useTableCellSelection(containerRef, {
    styleId: DSD_EDITOR_SELECTION_STYLE_ID,
    autoBindContainer: true,
    // 헤더(th)도 드래그 대상 — PlainDsdCellView 가 th 에도 선택 계약을 붙인다.
    includeHeaderCells: true,
    // 병합 셀이 많은 DSD 표라 드래그를 엑셀 그리드 시맨틱으로 처리한다.
    enableGridRangeSelection: true,
    clipboardTableFormat: "excel-table",
    enableArrowNavigation: false,
    selectionCss: MULTI_SELECT_CELL_BORDER_CSS,
    minSelectionToStyle: 2,
    onSelectionChange: options?.onMultiSelectChange
      ? (uniqueIds) => {
          options.onMultiSelectChange?.(uniqueIds.length >= 2);
        }
      : undefined,
  });
}
